import React, { useState, useMemo } from 'react';
import { GoogleMap, PolygonF, MarkerF, InfoWindowF, useLoadScript } from '@react-google-maps/api';

interface GoogleDestinationsMapProps {
    destinationsResponse?: string | null;
    address?: string;
    height?: string;
}

interface Destination {
    primary?: {
        displayPolygon?: {
            coordinates?: Array<Array<[number, number]>>;
            type?: string;
        };
        location?: {
            latitude?: number;
            longitude?: number;
        };
        formattedAddress?: string;
        structureType?: string;
    };
    navigationPoints?: Array<{
        displayName?: {
            text?: string;
            languageCode?: string;
        };
        location?: {
            latitude?: number;
            longitude?: number;
        };
        travelModes?: string[];
    }>;
}

const GoogleDestinationsMap: React.FC<GoogleDestinationsMapProps> = ({
    destinationsResponse,
    address,
    height = '400px'
}) => {
    const [selectedDestinationIndex, setSelectedDestinationIndex] = useState<number>(0);
    const [selectedNavigationPointIndex, setSelectedNavigationPointIndex] = useState<number | null>(null);
    const [streetViewPoint, setStreetViewPoint] = useState<{ lat: number; lng: number } | null>(null);

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''
    });

    const parsedData = useMemo(() => {
        if (!destinationsResponse) return null;
        try {
            const parsed = JSON.parse(destinationsResponse);
            return parsed.destinations || [];
        } catch {
            return null;
        }
    }, [destinationsResponse]);

    const selectedDestination: Destination | null = useMemo(() => {
        if (!parsedData || parsedData.length === 0) return null;
        return parsedData[selectedDestinationIndex] || null;
    }, [parsedData, selectedDestinationIndex]);

    const mapCenter = useMemo(() => {
        if (!selectedDestination?.primary?.location) {
            return { lat: 39.8283, lng: -98.5795 };
        }
        const { latitude, longitude } = selectedDestination.primary.location;
        return {
            lat: latitude || 39.8283,
            lng: longitude || -98.5795
        };
    }, [selectedDestination]);

    const polygonCoordinates = useMemo(() => {
        if (!selectedDestination?.primary?.displayPolygon?.coordinates) return [];
        // displayPolygon.coordinates is in GeoJSON format: Array<Array<[lng, lat]>>
        const coords = selectedDestination.primary.displayPolygon.coordinates[0] || [];
        return coords.map((coord: [number, number]) => ({
            lat: coord[1],
            lng: coord[0]
        }));
    }, [selectedDestination]);

    const entrancePoints = useMemo(() => {
        if (!selectedDestination?.navigationPoints) return [];
        return selectedDestination.navigationPoints
            .filter((navPoint: any) => navPoint.location?.latitude && navPoint.location?.longitude)
            .map((navPoint: any) => ({
                lat: navPoint.location.latitude,
                lng: navPoint.location.longitude,
                displayName: navPoint.displayName?.text,
                travelModes: navPoint.travelModes || []
            }));
    }, [selectedDestination]);

    if (loadError) {
        return (
            <div className="card">
                <div className="card-body text-center text-danger">
                    <i className="bi bi-exclamation-triangle fs-1"></i>
                    <p className="mt-2">Error loading Google Maps</p>
                </div>
            </div>
        );
    }

    if (!isLoaded) {
        return (
            <div className="card">
                <div className="card-body text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Loading map...</p>
                </div>
            </div>
        );
    }

    if (!parsedData || parsedData.length === 0) {
        return (
            <div className="card shadow-sm">
                <div className="card-header bg-light fw-bold">Google Destinations Preview</div>
                <div className="card-body text-center py-5">
                    <i className="bi bi-geo-alt fs-1 text-muted opacity-50"></i>
                    <p className="mt-2 text-muted">No destination data available</p>
                    {address && <div className="small text-muted">{address}</div>}
                </div>
            </div>
        );
    }

    return (
        <div className="card shadow-sm">
            <div className="card-header bg-light">
                <div className="fw-bold mb-3">Google Destinations (Beta)</div>

                {/* Destination Selector */}
                {parsedData.length > 1 && (
                    <div className="mb-3">
                        <label className="small text-uppercase text-muted fw-bold d-block mb-2">
                            Select Destination ({selectedDestinationIndex + 1} of {parsedData.length})
                        </label>
                        <div className="btn-group w-100" role="group">
                            {parsedData.map((dest: Destination, idx: number) => (
                                <button
                                    key={idx}
                                    type="button"
                                    className={`btn btn-sm ${
                                        selectedDestinationIndex === idx
                                            ? 'btn-primary'
                                            : 'btn-outline-secondary'
                                    }`}
                                    onClick={() => setSelectedDestinationIndex(idx)}
                                    title={dest.primary?.formattedAddress || `Destination ${idx + 1}`}
                                >
                                    {idx + 1}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div className="card-body p-0">
                <div style={{ height }} ref={el => el}>
                    {selectedDestination && (
                        <GoogleMap
                            mapContainerStyle={{
                                width: '100%',
                                height: '100%'
                            }}
                            center={mapCenter}
                            zoom={18}
                            options={{
                                streetViewControl: false,
                                mapTypeControl: true,
                                fullscreenControl: true,
                                zoomControl: true
                            }}
                        >
                            {/* Building Polygon */}
                            {polygonCoordinates.length > 0 && (
                                <PolygonF
                                    paths={polygonCoordinates}
                                    options={{
                                        fillColor: '#4285F4',
                                        fillOpacity: 0.25,
                                        strokeColor: '#1967D2',
                                        strokeOpacity: 0.8,
                                        strokeWeight: 2
                                    }}
                                />
                            )}

                            {/* Navigation Points Markers */}
                            {entrancePoints.map((point: any, idx: number) => {
                                const isSelected = selectedNavigationPointIndex === idx;
                                return (
                                    <React.Fragment key={`entrance-${idx}`}>
                                        <MarkerF
                                            position={{ lat: point.lat, lng: point.lng }}
                                            title={point.displayName || `Navigation Point ${idx + 1}`}
                                            onClick={() => setSelectedNavigationPointIndex(isSelected ? null : idx)}
                                            icon={{
                                                url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                                                    <svg width="${isSelected ? 40 : 32}" height="${isSelected ? 40 : 32}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
                                                        <circle cx="16" cy="16" r="${isSelected ? 16 : 14}" fill="${isSelected ? '#FF4444' : '#FF6B6B'}" stroke="#fff" stroke-width="${isSelected ? 3 : 2}"/>
                                                        <text x="16" y="20" text-anchor="middle" font-size="12" fill="#fff" font-weight="bold">${idx + 1}</text>
                                                    </svg>
                                                `),
                                                scaledSize: new window.google.maps.Size(isSelected ? 40 : 32, isSelected ? 40 : 32),
                                                anchor: new window.google.maps.Point(isSelected ? 20 : 16, isSelected ? 20 : 16)
                                            }}
                                        />
                                        {isSelected && (
                                            <InfoWindowF
                                                position={{ lat: point.lat, lng: point.lng }}
                                                onCloseClick={() => setSelectedNavigationPointIndex(null)}
                                                options={{
                                                    pixelOffset: new window.google.maps.Size(0, -40)
                                                }}
                                            >
                                                <div style={{ color: '#000', padding: '12px', maxWidth: '300px' }}>
                                                    <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}>
                                                        {point.displayName || `Navigation Point ${idx + 1}`}
                                                    </div>
                                                    <div style={{ fontSize: '12px', marginBottom: '8px' }}>
                                                        <div><strong>Coordinates:</strong></div>
                                                        <div>{point.lat.toFixed(6)}, {point.lng.toFixed(6)}</div>
                                                    </div>
                                                    {point.travelModes && (
                                                        <div style={{ fontSize: '12px', marginBottom: '10px' }}>
                                                            <div><strong>Travel Modes:</strong></div>
                                                            <div>{point.travelModes.join(', ')}</div>
                                                        </div>
                                                    )}
                                                    <button
                                                        onClick={() => setStreetViewPoint({ lat: point.lat, lng: point.lng })}
                                                        style={{
                                                            width: '100%',
                                                            padding: '6px 12px',
                                                            backgroundColor: '#4285F4',
                                                            color: '#fff',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            fontSize: '12px',
                                                            fontWeight: '600',
                                                            cursor: 'pointer',
                                                            transition: 'background-color 0.2s'
                                                        }}
                                                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#3367D6')}
                                                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#4285F4')}
                                                    >
                                                        <i className="bi bi-street-view me-2"></i>View Street
                                                    </button>
                                                </div>
                                            </InfoWindowF>
                                        )}
                                    </React.Fragment>
                                );
                            })}

                            {/* Center Location Pin */}
                            {selectedDestination?.primary?.location && entrancePoints.length === 0 && (
                                <MarkerF
                                    position={mapCenter}
                                    title={selectedDestination.primary.formattedAddress || 'Location'}
                                    icon={{
                                        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                                            <svg width="40" height="40" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40">
                                                <circle cx="20" cy="20" r="16" fill="#4285F4" stroke="#fff" stroke-width="2"/>
                                                <circle cx="20" cy="20" r="6" fill="#fff"/>
                                            </svg>
                                        `),
                                        scaledSize: new window.google.maps.Size(40, 40),
                                        anchor: new window.google.maps.Point(20, 20)
                                    }}
                                />
                            )}
                        </GoogleMap>
                    )}
                </div>
            </div>

            {/* Navigation Points List */}
            {entrancePoints.length > 0 && (
                <div className="card-body border-top">
                    <div className="small fw-bold mb-3 text-muted text-uppercase">Navigation Points ({entrancePoints.length})</div>
                    <div className="d-flex flex-column gap-2">
                        {entrancePoints.map((point: any, idx: number) => (
                            <button
                                key={`navpoint-${idx}`}
                                className={`btn btn-sm text-start ${
                                    selectedNavigationPointIndex === idx
                                        ? 'btn-primary'
                                        : 'btn-outline-secondary'
                                }`}
                                onClick={() => setSelectedNavigationPointIndex(selectedNavigationPointIndex === idx ? null : idx)}
                                style={{
                                    padding: '10px 12px',
                                    borderRadius: '6px',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'space-between' }}>
                                    <div>
                                        <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>
                                            {point.displayName || `Navigation Point ${idx + 1}`}
                                        </div>
                                        {point.travelModes && point.travelModes.length > 0 && (
                                            <div style={{ fontSize: '12px', opacity: 0.8 }}>
                                                {point.travelModes.map((mode: string) => (
                                                    <span key={mode} className="badge bg-secondary-subtle text-secondary me-1" style={{ fontSize: '11px', marginTop: '2px' }}>
                                                        {mode}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ flexShrink: 0, marginLeft: '8px' }}>
                                        <span style={{ fontSize: '16px', fontWeight: 'bold', backgroundColor: '#FF6B6B', color: '#fff', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {idx + 1}
                                        </span>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Legend */}
            <div className="card-footer bg-light small">
                <div className="row g-3">
                    <div className="col-auto">
                        <div className="d-flex align-items-center gap-2">
                            <div
                                style={{
                                    width: '20px',
                                    height: '20px',
                                    backgroundColor: '#4285F4',
                                    opacity: 0.25,
                                    border: '2px solid #1967D2',
                                    borderRadius: '2px'
                                }}
                            ></div>
                            <span>Building Polygon</span>
                        </div>
                    </div>
                    {entrancePoints.length > 0 && (
                        <div className="col-auto">
                            <div className="d-flex align-items-center gap-2">
                                <div
                                    style={{
                                        width: '20px',
                                        height: '20px',
                                        backgroundColor: '#FF6B6B',
                                        borderRadius: '50%',
                                        border: '2px solid #fff'
                                    }}
                                ></div>
                                <span>Navigation Points ({entrancePoints.length})</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Street View Modal */}
            {streetViewPoint && (
                <div
                    className="modal fade show"
                    style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.7)' }}
                    tabIndex={-1}
                    role="dialog"
                    onClick={() => setStreetViewPoint(null)}
                >
                    <div
                        className="modal-dialog modal-xl modal-dialog-centered"
                        onClick={(e) => e.stopPropagation()}
                        style={{ maxWidth: '90vw' }}
                    >
                        <div className="modal-content border-0 shadow-lg">
                            <div className="modal-header border-bottom">
                                <h5 className="modal-title">
                                    <i className="bi bi-street-view me-2"></i>Street View
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    aria-label="Close"
                                    onClick={() => setStreetViewPoint(null)}
                                ></button>
                            </div>
                            <div className="modal-body p-0" style={{ height: '70vh', overflow: 'hidden' }}>
                                <iframe
                                    width="100%"
                                    height="100%"
                                    src={`https://www.google.com/maps/embed/v1/streetview?location=${streetViewPoint.lat},${streetViewPoint.lng}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}`}
                                    style={{ border: 'none' }}
                                    allowFullScreen
                                    loading="lazy"
                                ></iframe>
                            </div>
                            <div className="modal-footer bg-light border-top">
                                <p className="text-muted small mb-0">
                                    Latitude: {streetViewPoint.lat.toFixed(6)}, Longitude: {streetViewPoint.lng.toFixed(6)}
                                </p>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setStreetViewPoint(null)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default GoogleDestinationsMap;
